import {
  Box,
  CircularProgress,
  InputBase,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  HistoryEduOutlined as AuditIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { useCallback, useEffect, useRef, useState } from "react";
import { getAuditLogs } from "../services/system/AuditLogService";
import { useAuth } from "../contexts/AuthContext";
import { useMessageContext } from "../contexts/MessageContext";
import { FilterDropdown, Pagination } from "../componenets/ui";
import { t } from "../componenets/dashboard/coach/coachTokens";
import moment from "moment";
import "moment/locale/he";

moment.locale("he");

const PAGE_SIZE = 30;

/* ── Table cell presets (match Clients / Companies / Messages) ── */
const td = { padding: "10px 12px", borderBottom: `1px solid ${t.borderLight}`, fontSize: "12.5px", color: t.text, verticalAlign: "middle" };
const th = { padding: "10px 12px", fontSize: "10.5px", fontWeight: 700, color: t.t3, borderBottom: `1px solid ${t.border}`,
             background: t.bg, whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: ".06em" };

/* Action -> Hebrew label. Mirrors AuditAction on the server. */
const ACTION_LABELS = {
  USER_CREATED: "משתמש נוצר",
  USER_UPDATED: "משתמש עודכן",
  USER_DELETED: "משתמש נמחק",
  PASSWORD_CHANGED: "סיסמה שונתה",
  PASSWORD_RESET_REQUESTED: "בקשת איפוס סיסמה",
  PASSWORD_RESET_COMPLETED: "איפוס סיסמה הושלם",
  PROGRAM_CHANGED: "תוכנית שונתה",
  EXERCISE_REPLACED: "תרגיל הוחלף",
  EXERCISE_REMOVED: "תרגיל הוסר",
  COMPANY_DELETED: "חברה נמחקה",
};

/* Options for the action filter dropdown (FilterDropdown expects {value,label}). */
const ACTION_OPTS = [
  { value: "", label: "כל הפעולות" },
  ...Object.entries(ACTION_LABELS).map(([value, label]) => ({ value, label })),
];

/* ActorRole -> Hebrew label + color. Mirrors ActorRole on the server. */
const ROLE_META = {
  SELF: { label: "המשתמש עצמו", bg: t.blueLight, fg: t.blueText },
  TRAINER: { label: "מאמן", bg: t.greenLight, fg: t.greenText },
  ADMIN: { label: "מנהל", bg: t.amberLight, fg: t.amberText },
  SYSTEM_ADMIN: { label: "מנהל על", bg: t.purpleLight, fg: t.purple },
  SYSTEM: { label: "מערכת", bg: t.bg, fg: t.t2 },
};

const RoleChip = ({ role }) => {
  const meta = ROLE_META[role] || { label: role || "—", bg: t.bg, fg: t.t2 };
  return (
    <Box component="span" sx={{
      display: "inline-flex", alignItems: "center", fontSize: "11px", fontWeight: 700,
      color: meta.fg, background: meta.bg, padding: "3px 10px", borderRadius: "20px", whiteSpace: "nowrap",
    }}>
      {meta.label}
    </Box>
  );
};

/* Compact "name / email (#id)" identity cell — survives deleted users since these are snapshots. */
const Identity = ({ name, email, id }) => {
  if (!id && !name && !email) {
    return <Typography component="span" sx={{ color: t.t3 }}>—</Typography>;
  }
  return (
    <Box>
      <Typography sx={{ fontSize: "12.5px", fontWeight: 600, color: t.text }}>
        {name || "—"} {id ? <Box component="span" sx={{ color: t.t3, fontWeight: 500 }}>#{id}</Box> : null}
      </Typography>
      {email ? <Typography sx={{ fontSize: "11px", color: t.t2 }}>{email}</Typography> : null}
    </Box>
  );
};

const AuditLogPage = () => {
  const { isSuperAdmin } = useAuth();
  const { notificationHandler } = useMessageContext();

  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);

  const [actionFilter, setActionFilter] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [keyword, setKeyword] = useState("");
  const debounceRef = useRef(null);

  const handleSearch = (val) => {
    setSearchInput(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(0);
      setKeyword(val.trim());
    }, 300);
  };

  const fetchPage = useCallback((pageId) => {
    setLoading(true);
    getAuditLogs(pageId, PAGE_SIZE, {
      action: actionFilter || undefined,
      keyword: keyword || undefined,
    })
      .then((res) => {
        const data = res.data || {};
        setRows(data.content || []);
        setTotal(data.totalElements ?? 0);
        setTotalPages(data.totalPages ?? 0);
        setPage(pageId);
      })
      .catch(() => notificationHandler.error("טעינת לוג הביקורת נכשלה"))
      .finally(() => setLoading(false));
  }, [actionFilter, keyword, notificationHandler]);

  useEffect(() => {
    fetchPage(0);
  }, [fetchPage]);

  if (!isSuperAdmin()) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
        <Typography sx={{ color: t.t3, fontSize: "15px", fontWeight: 500 }}>
          אין לך הרשאה לצפות בעמוד זה
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "18px" }}>
      {/* Title */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <AuditIcon sx={{ color: t.green, fontSize: "28px" }} />
          <Typography sx={{ fontSize: { xs: "22px", md: "26px" }, fontWeight: 800, color: t.text }}>
            לוג ביקורת
          </Typography>
        </Box>
      </Box>

      {/* Table card */}
      <Box sx={{ background: t.white, border: `1px solid ${t.border}`, borderRadius: t.r, boxShadow: t.sh,
                 overflow: "hidden", display: "flex", flexDirection: "column" }}>

        {/* Filter bar inside card (matches Clients) */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "7px", padding: "10px 14px",
                   borderBottom: `1px solid ${t.border}`, background: t.bg, flexWrap: "wrap", flexShrink: 0 }}>
          {/* Count pill */}
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: "4px", fontSize: "12px", fontWeight: 700,
                     color: t.greenText, background: t.greenLight, border: "1px solid rgba(1,187,167,.2)",
                     padding: "4px 12px", borderRadius: "20px", whiteSpace: "nowrap" }}>
            <AuditIcon sx={{ fontSize: 14 }} />
            {total} רשומות
          </Box>

          {/* Search by name / email */}
          <Box sx={{ position: "relative" }}>
            <SearchIcon sx={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: t.t3, pointerEvents: "none" }} />
            <InputBase
              value={searchInput}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="חיפוש לפי שם, אימייל..."
              sx={{
                width: 220,
                padding: "6px 10px 6px 30px",
                border: `1.5px solid ${t.border}`,
                borderRadius: t.rs,
                background: t.white,
                color: t.text,
                fontSize: "12px",
                "& input": { padding: 0 },
                "&:focus-within": { borderColor: t.green, boxShadow: "0 0 0 3px rgba(1,187,167,.08)" },
              }}
            />
          </Box>

          {/* Action filter */}
          <FilterDropdown
            label="פעולה"
            value={actionFilter}
            onChange={(v) => { setPage(0); setActionFilter(v); }}
            options={ACTION_OPTS}
            minWidth={160}
          />

          <Box sx={{ flex: 1 }} />
        </Box>

        {loading && !rows.length ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 480 }}>
            <CircularProgress size={32} sx={{ color: t.green }} />
          </Box>
        ) : !rows.length ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 240, flexDirection: "column", gap: 1, padding: "48px" }}>
            <Typography sx={{ color: t.t3, fontSize: "13px", fontWeight: 500 }}>אין רשומות להצגה</Typography>
            <Typography sx={{ color: t.t3, fontSize: "12px" }}>נסה לשנות את הסינון או החיפוש</Typography>
          </Box>
        ) : (
          <>
            <TableContainer sx={{ overflowX: "auto", overflowY: "auto", maxHeight: 600, direction: "rtl" }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...th, textAlign: "right" }}>תאריך ושעה</TableCell>
                    <TableCell sx={{ ...th, textAlign: "right" }}>פעולה</TableCell>
                    <TableCell sx={{ ...th, textAlign: "center" }}>מי ביצע</TableCell>
                    <TableCell sx={{ ...th, textAlign: "right" }}>מבצע הפעולה</TableCell>
                    <TableCell sx={{ ...th, textAlign: "right" }}>נושא הפעולה</TableCell>
                    <TableCell sx={{ ...th, textAlign: "right" }}>פרטים</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell sx={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
                        {r.createdDate ? moment.unix(r.createdDate).format("DD/MM/YY HH:mm") : "—"}
                      </TableCell>
                      <TableCell sx={{ ...td, textAlign: "right", fontWeight: 600 }}>
                        {ACTION_LABELS[r.action] || r.action}
                      </TableCell>
                      <TableCell sx={{ ...td, textAlign: "center" }}>
                        <RoleChip role={r.actorRole} />
                      </TableCell>
                      <TableCell sx={{ ...td, textAlign: "right" }}>
                        <Identity name={r.actorName} email={r.actorEmail} id={r.actorUserId} />
                      </TableCell>
                      <TableCell sx={{ ...td, textAlign: "right" }}>
                        <Identity name={r.subjectName} email={r.subjectEmail} id={r.subjectUserId} />
                      </TableCell>
                      <TableCell sx={{ ...td, textAlign: "right", color: t.t2, maxWidth: 280 }}>
                        {r.details || "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Pagination
              currentPage={page}
              totalPages={totalPages}
              totalItems={total}
              itemsPerPage={PAGE_SIZE}
              onPageChange={(p) => fetchPage(p)}
            />
          </>
        )}
      </Box>
    </Box>
  );
};

export default AuditLogPage;
